import prepareTranslate from '@/helpers/dictionary';
import routes from '@/routes';
import useAccessToken from '@/hooks/useAccessToken';
import PageTemplate from '@/components/PageTemplate';
import { MonthPickerInput, YearPickerInput } from '@mantine/dates';
import { objectToQueryString } from '@/helpers/string';
import { useForm } from '@mantine/form';
import validations from '@/helpers/validation';
import { useEffect, useState } from 'react';
import { useDebouncedCallback } from '@mantine/hooks';
import { useNavigate } from 'react-router-dom';
import { routeWithParams } from '@/helpers/route';
import exportRuleQuery from '@/api/queries/exportRule';
import companyQuery from '@/api/queries/company';
import productQuery from '@/api/queries/product';
import agentMutations from '@/api/mutitations/agent';
import { notifications } from '@mantine/notifications';
import { createArraysFromRange, createYearArraysFromRange } from '@/helpers/dates';
import {
  Button,
  Grid,
  MultiSelect,
  Paper,
  Select,
  Text,
  Loader,
  Autocomplete,
  Container,
  Center,
  Stack,
} from '@mantine/core';
import { CATEGORIES, ACCESORS } from '@/helpers/enums';
import headerQuery from '@/api/queries/header';
import { startOfMonth, addMonths, subMonths } from 'date-fns';

const Reports = () => {
  useAccessToken();

  const { data: exportRuleData } = exportRuleQuery.useGetAll();
  const { data: companyData } = companyQuery.useGetAll();
  const { data: productData } = productQuery.useGetAll();
  const { data: headersData, isLoading } = headerQuery.useFindByAccessor(ACCESORS);
  const { mutateAsync: agentMutate } = agentMutations.useGetAgents();

  const navigate = useNavigate();

  const t = prepareTranslate();
  const [loading, setLoading] = useState(false);
  const [empty, setEmpty] = useState(false);
  const [data, setData] = useState([]);
  const [reportId, setReportId] = useState('');
  const [lastReport, setLastReport] = useState('');
  const [lastCategory, setLastCategory] = useState('');
  const [autoCompleteValue, setAutoCompleteValue] = useState('');
  const [pickedAgent, setPickedAgent] = useState();
  const [dropdownOpened, setDropdownOpened] = useState(false);
  const [filterCategories, setFilterCategories] = useState(CATEGORIES);
  const categoriesWithRangePicker = ['agent', 'date'];

  /*
   * TODO: activate favorites when finish developing
  const tabs = [
    {
      title: t('pages.reports.side.favorites'),
      content: <Favorites entity='report' />,
      isDefault: true,
    },
  ];
  */

  const handleFormSubmit = () => {
    const { hasErrors, errors } = form.validate();

    if (hasErrors) {
      const firstErrorPath = Object.keys(errors)[0];
      form.getInputNode(firstErrorPath)?.focus();
      return;
    }

    let path;
    const values = form.getValues();

    if (values.company === 'all') {
      values.company = [];
    } else {
      values.company = [values.company];
    }

    if (values.product[0] === 'all') {
      values.product = [];
    }

    if (!Array.isArray(values.period)) {
      values.period = [values.period];
    }

    // Use the appropriate function based on whether we're selecting years or months
    const [yearArray, monthArray] = reportId.useYearPicker
      ? createYearArraysFromRange(...values.period)
      : createArraysFromRange(...values.period);

    const queryData = {
      month: monthArray,
      year: yearArray,
      company: values.company,
      product: values.product,
      category: values.category,
      showChart: reportId.showChart,
      useDateSelectors: reportId.useDateSelectors,
    };

    if (autoCompleteValue !== 'כל הסוכנים') {
      queryData.agent = [pickedAgent];
    } else {
      queryData.agent = [];
    }

    const query = objectToQueryString(queryData);

    if (queryData.agent.length > 1) {
      path = routes.reportList.path;
    } else {
      path = routes.reportsView.path;
    }

    navigate(routeWithParams(path, { id: reportId._id, query }));
  };

  const handleOptionSubmit = (val) => {
    setPickedAgent(val);
  };

  const handleReportSelect = (report, category) => {
    if (report !== lastReport) {
      changeReport(report, category);
      form.setFieldValue('report', report);
      setLastReport(report);
    } else {
      setLastReport('');
    }
  };

  const changeReport = (reportCode, selectedCategory) => {
    if (!exportRuleData) {
      return;
    }

    // eslint-disable-next-line max-len
    const relevantRules = exportRuleData?.filter((rule) => rule.columns.some((col) => col.key === true && col.title._id === reportCode));

    const ruleCategories = relevantRules.map((rule) => rule.category || rule.cateory);
    // eslint-disable-next-line max-len
    const matchedCategories = CATEGORIES.filter((category) => ruleCategories.includes(category.value));

    setFilterCategories(matchedCategories);
    if (!ruleCategories.includes(form.values.category)) {
      form.setFieldValue('category', null);
      setLastCategory(null);
    }
    // eslint-disable-next-line max-len
    const matchingRule = exportRuleData?.find((rule) => rule.category === selectedCategory && rule.columns.some((col) => col.key === true && col.title._id === reportCode));

    if (matchingRule) {
      setReportId(matchingRule);
    }
  };

  const handleCategoryChange = (report, category) => {
    if (categoriesWithRangePicker.includes(category)) {
      form.setValues({ period: [] });
    } else {
      form.setValues({ period: null });
    }
    if (category !== lastCategory) {
      changeReport(report, category);
      setLastCategory(category);
    } else {
      setLastCategory('');
    }
  };

  const handleProductsSelectChange = (selected) => {
    if (selected.includes('all')) {
      form.setFieldValue('product', ['all']);
    } else {
      form.setFieldValue('product', selected);
    }
  };

  const handleAutocompleteChange = (val) => {
    if (pickedAgent) {
      setAutoCompleteValue('');
      setData([]);
      setPickedAgent(null);
      setDropdownOpened(false);
    } else {
      const hasAgent = data.find((agent) => agent.label === val);
      setAutoCompleteValue(val);
      setDropdownOpened(true);

      if (!hasAgent) {
        autoCompleteQuery(val);
      } else {
        setData([]);
      }
    }
  };

  const handleAutocompleteFocus = async () => {
    setDropdownOpened(true);
    if (data.length === 0) {
      setLoading(true);
      try {
        const { data: agentData } = await agentMutate({ query: {}, start: 1 });
        const allAgentsOption = { label: t('pages.reports.labels.agents'), value: 'all' };
        setData([
          allAgentsOption,
          ...agentData.map((agent) => ({
            label: `${agent.personalDetails.fullName} (${agent.personalDetails.uid})`,
            value: agent._id,
          })),
        ]);
      } catch {
        // silent fail - user can still type to search
      } finally {
        setLoading(false);
      }
    }
  };

  const autoCompleteQuery = useDebouncedCallback(async (val) => {
    setLoading(true);
    setEmpty(false);

    if (val === '') {
      setData([]);
    } else {
      const start = 1;
      const query = { 'personalDetails.fullName': val };

      try {
        const { data: agentData } = await agentMutate({ query, start });
        const allAgentsOption = { label: t('pages.reports.labels.agents'), value: 'all' };
        if (agentData.length === 0) {
          setData([allAgentsOption]);
          setEmpty(true);
        } else {
          setData([
            allAgentsOption,
            ...agentData.map((agent) => ({
              label: `${agent.personalDetails.fullName} (${agent.personalDetails.uid})`,
              value: agent._id,
            })),
          ]);
        }
      } catch {
        notifications.show({
          title: t('pages.reports.notification.error.title'),
          message: t('pages.reports.notification.error.message'),
          color: 'red',
        });
      }
    }

    setLoading(false);
  }, 500);

  const actions = [
    <Button key='primary'
      onClick={handleFormSubmit}
      mt={'2rem'}
      mr={'1rem'}
      w={'7rem'}
    >{t('pages.reports.action')}
    </Button>,
  ];

  const headrsSelect = !isLoading && headersData && headersData
    .map((exportRule) => ({
      label: exportRule.header,
      value: exportRule._id,
      accessor: exportRule.accessor,
    }))
    .filter((exportRule) => ACCESORS.includes(exportRule.accessor))
    .sort((a, b) => ACCESORS.indexOf(a.accessor) - ACCESORS.indexOf(b.accessor));
  const companySelect = (companyData && companyData
    .map((company) => ({ label: company.hebrew, value: company._id }))) || [];
  companySelect.push({ label: t('pages.reports.labels.companies'), value: 'all' });
  const productSelect = (productData && productData
    .map((product) => ({ label: product.hebrew, value: product._id }))) || [];
  productSelect.push({ label: t('pages.reports.labels.products'), value: 'all' });

  const startOfNextMonth = startOfMonth(addMonths(new Date(), 1));
  const previousMonth = startOfMonth(subMonths(new Date(), 1));

  const form = useForm({
    mode: 'controlled',
    initialValues: {
      report: null,
      category: CATEGORIES.find((category) => category.value === 'company').value,
      company: 'all',
      product: [],
      period: previousMonth,

    },
    validate: {
      report: (value) => validations.required(value),
      category: (value) => validations.required(value),
      agent: () => validations.agent(pickedAgent),
      company: (value) => validations.company(value),
      product: (value) => validations.product(value),
      period: (value) => validations.required(value),
    },
  });

  const maxProductValues = form.values.product.includes('all') ? 1 : 3;
  const monthPickerType = categoriesWithRangePicker.includes(form.values.category) ? 'range' : 'default';

  useEffect(() => {
    if (!headrsSelect || headrsSelect.length === 0 || !exportRuleData) return;
    if (form.values.report) return;

    const category = CATEGORIES.find((c) => c.value === 'company').value;
    form.setFieldValue('report', headrsSelect[0]?.value);
    handleReportSelect(headrsSelect[0]?.value, form.values.category);
    const matchingRule = exportRuleData?.find(
      (rule) => rule.category === category && rule.columns.some(
        (col) => col.key === true && col.title._id === headrsSelect[0]?.value,
      ),
    );

    setReportId(matchingRule);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headrsSelect, exportRuleData]);

  return (
    <>

      <PageTemplate
        tabsTitle={t('pages.reports.side.tabs')}
        title={routes.reports.name}
      >
        {isLoading ? (
          <Center style={{ height: '50vh' }
          } >
            <Loader size={50} />
          </Center>
        ) : (
          <PageTemplate.Panel id='form'>
            <form>
              <Paper p='xl' mt={'2rem'} mr={'1rem'}>
                <Grid gutter='lg' p={'1rem'} pb={'3rem'}>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Stack gap={'lg'}>
                      <Select
                        label={t('pages.reports.form.report')}
                        data={headrsSelect}
                        required
                        // eslint-disable-next-line max-len
                        onOptionSubmit={(option) => handleReportSelect(option, form.values.category)}
                        key={form.key('report')}
                        {...form.getInputProps('report')}
                      />
                      <Select
                        label={t('pages.reports.form.company')}
                        data={companySelect}
                        required
                        key={form.key('company')}
                        {...form.getInputProps('company')}
                      />
                      {reportId?.useYearPicker ? (
                        <YearPickerInput
                          type={monthPickerType}
                          label={t('pages.reports.form.daterange')}
                          required
                          key={form.key('period')}
                          {...form.getInputProps('period')}
                          maxDate={new Date()}
                        />
                      ) : (
                        <MonthPickerInput
                          type={monthPickerType}
                          label={t('pages.reports.form.daterange')}
                          required
                          key={form.key('period')}
                          {...form.getInputProps('period')}
                          maxDate={startOfNextMonth}
                        />
                      )}
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Stack gap={'lg'}>
                      <Select
                        label={t('pages.reports.form.category')}
                        data={filterCategories}
                        required
                        {...form.getInputProps('category')}
                        key={form.key('category')}
                        onOptionSubmit={(value) => handleCategoryChange(form.values.report, value)}
                        disabled={!form.values.report}
                      />

                      <MultiSelect
                        label={t('pages.reports.form.product')}
                        data={productSelect}
                        value={form.values.product}
                        required
                        maxValues={maxProductValues}
                        placeholder={t('pages.reports.placeHolders.products')}
                        key={form.key('product')}
                        {...form.getInputProps('product')}
                        onChange={handleProductsSelectChange}
                      />

                      <Autocomplete
                        label={t('pages.reports.form.agent')}
                        rightSection={loading ? <Loader size='1rem' /> : null}
                        data={data}
                        required
                        limit='10'
                        key={form.key('agent')}
                        {...form.getInputProps('agent')}
                        value={autoCompleteValue}
                        onChange={handleAutocompleteChange}
                        onOptionSubmit={handleOptionSubmit}
                        dropdownOpened={dropdownOpened}
                        onFocus={handleAutocompleteFocus}
                        onBlur={() => setDropdownOpened(false)}
                      />

                      {empty && (
                        <Container fluid mt='xs' bg='var(--mantine-color-blue-light)'>
                          <Text>{t('pages.reports.noAgents')}</Text>
                        </Container>
                      )}
                    </Stack>
                  </Grid.Col>

                </Grid>
              </Paper>
            </form>
            {actions}
          </PageTemplate.Panel>
        )} </PageTemplate>

    </>
  );
};

export default Reports;
